import MUIDataTable from "mui-datatables";
import { Vocabulary } from "../../Utils/Vocabulary";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Grid,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { ThemeProvider } from "@emotion/react";
import { getMuiTheme } from "../../Utils/globalStyleTables";
import { getData } from "../../Services/getData";
import { urlEnum } from "../../Utils/urlEnum";
import ModeEditIcon from "@mui/icons-material/ModeEdit";
import {
  AutocompleteUser,
  VINRequestModel,
  StatusModel,
} from "../../Models/Models";
import {
  Nomenclator,
  VehicleBrandModel_Model,
} from "../../Models/VehicleModel";
import moment, { Moment } from "moment";
import { europeanDateWithTime } from "../../Utils/formatDate";
import { postData } from "../../Services/postData";
import { PER_PAGE_DEFAULT, tableLayoutOnMobile } from "../../Utils/Constants";
import {
  exportTable,
  getFirstVehicleCreatedAt,
  isMobile,
} from "../../Utils/utils";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import TuneIcon from "@mui/icons-material/Tune";
import { DesktopDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import LegendComponent from "../LegendComponent";
import VINRequestModal from "./VINRequestModal";
import { CacheContext } from "../../Context/cacheContext";

type FormStateModel = {
  vin: string | null;
  brand: Nomenclator | null;
  model: VehicleBrandModel_Model | null;
  status: StatusModel[] | undefined;
  clientName: string | null;
  order: string | undefined;
  page: number;
  perPage: number;
  count: number;
  startDate: Moment | string;
  endDate: Moment | string;
  user: AutocompleteUser | undefined | null;
  accesForAllMessages?: any;
};

export interface MainTableProps {
  children?: React.ReactNode;
  index?: number;
  value?: number;
}

export default function VINRequests(props: any) {
  const [vinRequests, setVinRequests] = useState<Array<VINRequestModel>>([]);
  const [selected, setSelected] = useState<VINRequestModel | null>(null);
  const [openAccordion, setOpenAccordion] = useState(false);
  const [vinRequestStatus, setVinRequestStatus] = useState<number>();
  const [associatedUser, setAssociatedUser] = useState<
    number | null | undefined
  >();
  const userId = localStorage.getItem("useId");
  const cacheContext = useContext(CacheContext);
  const minDate = getFirstVehicleCreatedAt(cacheContext.cache);

  const [formState, setFormState] = useState<FormStateModel>({
    vin: "",
    brand: null,
    model: null,
    status: undefined,
    user: null,
    clientName: "",
    order: "desc",
    page: 0,
    perPage: PER_PAGE_DEFAULT,
    count: 0,
    startDate: moment().subtract(1, "months"),
    endDate: moment(),
  });

  /**
   * get requests on change
   */
  useEffect(() => {
    getRequests();
  }, [
    formState.page,
    formState.perPage,
    props.value,
    formState.brand,
    formState.model,
    formState.order,
    formState.status,
    formState.user,
    formState.startDate,
    formState.endDate,
  ]);

  /**
   * get requests from server and override local requests
   */
  async function getRequests(startDate?: any, endDate?: any) {
    const url = formAPIVinRequestsUrl(startDate, endDate);

    try {
      const response = await getData(url);
      setVinRequests(response.data.messages);
      props.setUnassignedRequestsCount(response.data.numberOfUnseenMessages);

      const newForm = Object.assign({}, formState);
      newForm.count = response.data.numberOfMessages;
      newForm.startDate = startDate ? startDate : formState.startDate;
      newForm.endDate = endDate ? endDate : formState.endDate;
      setFormState(newForm);
    } catch (e) {
      setVinRequests([]);
    }
  }

  /**
   * get the api url
   * @returns string
   */
  function formAPIVinRequestsUrl(startDate?: any, endDate?: any) {
    const statusIds: any[] = [];
    if (formState.status) {
      formState.status.forEach((status) => {
        statusIds.push(status.id);
      });
    }
    return `${urlEnum.clientMessages}?type=VINRequest&page=${
      formState.page
    }&perPage=${formState.perPage}&vin=${formState.vin}&brand_id=${
      formState.brand?.id || ""
    }&model_id=${formState.model?.id || ""}&clientName=${
      formState.clientName
    }&order=${formState.order}&startDate=${moment(
      startDate ? startDate : formState.startDate,
    ).format("YYYY-MM-DD")}&endDate=${moment(
      endDate ? endDate : formState.endDate,
    ).format(
      "YYYY-MM-DD",
    )}&firstSeenUser=${userId}&status=${null}&filterStatus=${statusIds}&filterUser=${
      formState.user?.id
    }`;
  }

  /**
   * changes date values
   * @param event
   */
  function onChangeStartDate(event: any) {
    const newFormState = Object.assign({}, formState);
    newFormState.startDate = event?._d;
    getRequests(event?._d, formState.endDate);
  }

  /**
   * changes date values
   * @param event
   */
  function onChangeEndDate(event: any) {
    const newFormState = Object.assign({}, formState);
    newFormState.endDate = event?._d;
    getRequests(formState.startDate, event?._d);
  }

  function onTextFieldKeyUp(e: React.KeyboardEvent) {
    if (e.key === "Enter") {
      getRequests();
    }
  }

  /**
   * Get status name from status ID by looking it up in the cache
   */
  function getStatusName(statusId: string | null | undefined): string {
    if (!statusId || !props.cacheContext?.cache?.vinRequestsStatus) {
      return "";
    }
    const status = props.cacheContext.cache.vinRequestsStatus.find(
      (s: StatusModel) => s.id === parseInt(statusId),
    );
    return status?.nume || "";
  }

  const columns = [
    {
      name: "phone",
      label: Vocabulary.phone,
      options: {
        filter: true,
        sort: false,
      },
    },
    {
      name: "status",
      label: Vocabulary.status,
      options: {
        filter: true,
        sort: false,
        customBodyRender: (status: StatusModel | undefined) => (
          <Typography>{status?.nume}</Typography>
        ),
      },
    },
    {
      name: "vehicle",
      label: Vocabulary.vehicle,
      options: {
        filter: true,
        sort: false,
        customBodyRenderLite: (rowIndex: number) => {
          return (
            <>
              {vinRequests[rowIndex]?.vin ? (
                <Typography>
                  {vinRequests[rowIndex].brand} {vinRequests[rowIndex].model}
                  <br />
                  {Vocabulary.vin}: {vinRequests[rowIndex].vin}
                </Typography>
              ) : null}
            </>
          );
        },
      },
    },
    {
      name: "created_at",
      label: Vocabulary.date,
      options: {
        filter: true,
        sort: false,
        downloadType: "date",
        customBodyRender: (date: string | null) => {
          const d = moment.utc(date);
          return (
            <Typography>{d.local().format(europeanDateWithTime)}</Typography>
          );
        },
      },
    },
    {
      name: "",
      label: Vocabulary.options,
      options: {
        filter: false,
        sort: false,
        download: false,
        customBodyRenderLite: (rowIndex: number) => (
          <div style={{ float: "right" }}>
            <Tooltip title={Vocabulary.edit}>
              <ModeEditIcon
                style={{
                  marginRight: 15,
                  height: 30,
                  cursor: "pointer",
                  color: "#4449",
                }}
                onClick={(event: any) => {
                  event.stopPropagation();
                  openRequest(rowIndex);
                }}
              />
            </Tooltip>
          </div>
        ),
      },
    },
  ];

  const options: any = {
    rowsPerPage: formState.perPage,
    page: formState.page,
    selectableRows: "none",
    responsive: tableLayoutOnMobile,
    filterType: "dropdown",
    filter: false,
    download: true,
    sort: false,
    serverSide: true,
    count: formState.count,
    viewColumns: false,
    fixedHeader: false,
    print: false,
    search: false,
    rowHover: false,
    rowsPerPageOptions: [15, 50, 100, 500],
    onChangeRowsPerPage: (numberOfRows: number) => {
      setFormState({
        ...formState,
        perPage: numberOfRows,
        page: 0,
      });
    },
    onChangePage: (page: number) => {
      setFormState({
        ...formState,
        page,
      });
    },
    onTableChange: (action: string, tableState: any) => {
      if (action === "changePage") {
        setFormState({
          ...formState,
          page: tableState.page,
        });
      }
    },
    onDownload: (_: any, __: any, columns: any, data: any) => {
      const customData = data.map((row: any) => [
        vinRequests[row.index].name,
        vinRequests[row.index].email,
        vinRequests[row.index].phone,
        vinRequests[row.index].vin,
        getStatusName(vinRequests[row.index].status),
        moment
          .utc(vinRequests[row.index].requested_at)
          .local()
          .format(europeanDateWithTime),
        vinRequests[row.index].first_seen_user?.name || "",
      ]);

      return exportTable("", customData, "VIN_Requests", columns);
    },
    onRowClick: (_: any, rowMeta: any) => {
      openRequest(rowMeta.rowIndex);
    },
    setRowProps: (row: [], dataIndex: number, rowIndex: number) => {
      let styleProps = {};

      if ((vinRequests[rowIndex]?.status as any)?.culoare) {
        styleProps = {
          ...styleProps,
          backgroundColor: (vinRequests[rowIndex]?.status as any)?.culoare,
        };
      }
      return {
        style: { ...styleProps, cursor: "pointer" },
      };
    },
  };

  /**
   * opens a request detail modal
   * @param rowIndex
   */
  function openRequest(rowIndex: number) {
    const request = vinRequests[rowIndex];
    setSelected(request);
    setVinRequestStatus(
      request.status ? parseInt(request.status_id) : undefined,
    );
    setAssociatedUser(request.first_seen_user_id);
  }

  /**
   * render modal using VINRequestModal component
   */
  function renderModal() {
    return (
      <VINRequestModal
        open={!!selected}
        selected={selected}
        vinRequestStatus={vinRequestStatus}
        setVinRequestStatus={setVinRequestStatus}
        onClose={() => {
          setSelected(null);
          getRequests();
        }}
        onSave={(id) => {
          setSelected(null);
          handleChangeVinRequestStatus(id);
        }}
        cacheContext={props.cacheContext}
      />
    );
  }

  /**
   * changes the status of a vin request
   */
  async function handleChangeVinRequestStatus(id: number | undefined) {
    const url = `${urlEnum.clientMessages}/updateStatus/${id}/${vinRequestStatus}/${associatedUser}`;
    try {
      await postData(url);
      getRequests();
    } catch (e) {
      setVinRequests([]);
    }
  }

  /**
   * legend rendering
   */
  function renderLegend() {
    return (
      <LegendComponent
        title={Vocabulary.status}
        data={props.cacheContext.cache.clientMessagesStatus}
      />
    );
  }

  /**
   * render filter form
   */
  function renderFilterForm() {
    return (
      <>
        <Grid
          container
          spacing={3}
          style={{ display: "flex", flexDirection: "column" }}
        >
          <Grid item lg={12} md={12} sm={12} xs={12}>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DesktopDatePicker
                label={Vocabulary.fromDate}
                format="YYYY-MM-DD"
                value={formState.startDate}
                minDate={moment(minDate)}
                maxDate={moment(formState?.endDate)}
                onChange={onChangeStartDate}
                slotProps={{
                  textField: { size: "small", fullWidth: true },
                }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item lg={12} md={12} sm={12} xs={12}>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DesktopDatePicker
                label={Vocabulary.toDate}
                format="YYYY-MM-DD"
                value={formState.endDate}
                minDate={moment(formState?.startDate)}
                maxDate={moment()}
                onChange={onChangeEndDate}
                slotProps={{
                  textField: { size: "small", fullWidth: true },
                }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item lg={12} md={12} sm={12} xs={12}>
            <TextField
              label={Vocabulary.vin}
              value={formState.vin || ""}
              onChange={(e) => {
                setFormState({
                  ...formState,
                  vin: e.target.value.toUpperCase(),
                  page: 0,
                });
              }}
              onKeyUp={onTextFieldKeyUp}
              onBlur={() => getRequests()}
              size="small"
              fullWidth
            />
          </Grid>
          <Grid item lg={12} md={12} sm={12} xs={12}>
            <Autocomplete
              options={props.cacheContext?.cache?.brands || []}
              getOptionLabel={(option: Nomenclator) => option.nume || ""}
              value={formState.brand || null}
              onChange={(_, newValue) => {
                setFormState({
                  ...formState,
                  brand: newValue,
                  model: null,
                  page: 0,
                });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={Vocabulary.brand}
                  size="small"
                  fullWidth
                />
              )}
            />
          </Grid>
          <Grid item lg={12} md={12} sm={12} xs={12}>
            <Autocomplete
              options={
                formState.brand
                  ? (
                      props.cacheContext?.cache
                        ?.models as VehicleBrandModel_Model[]
                    )?.filter(
                      (m: VehicleBrandModel_Model) =>
                        m.brand_id === formState.brand?.id,
                    ) || []
                  : []
              }
              getOptionLabel={(option: VehicleBrandModel_Model) =>
                option.nume || ""
              }
              value={formState.model || null}
              onChange={(_, newValue) => {
                setFormState({
                  ...formState,
                  model: newValue,
                  page: 0,
                });
              }}
              disabled={!formState.brand}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={Vocabulary.model}
                  size="small"
                  fullWidth
                />
              )}
            />
          </Grid>

          {props.isAdmin && (
            <Grid item lg={12} md={12} sm={12} xs={12}>
              <Autocomplete
                multiple
                options={props.cacheContext?.cache?.vinRequestsStatus || []}
                getOptionLabel={(option: StatusModel) => option.nume || ""}
                value={formState.status || []}
                onChange={(_, newValue) => {
                  setFormState({
                    ...formState,
                    status: newValue,
                    page: 0,
                  });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={Vocabulary.status}
                    size="small"
                    fullWidth
                  />
                )}
              />
            </Grid>
          )}
        </Grid>
        {renderLegend()}
      </>
    );
  }

  return (
    <Grid container spacing={3} style={{ padding: isMobile() ? 0 : 20 }}>
      <Grid item lg={2} md={2} sm={12} xs={12}>
        {isMobile() ? (
          <Accordion
            expanded={openAccordion}
            onChange={() => setOpenAccordion(!openAccordion)}
            style={{ zIndex: 10 }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel1a-content"
              id="panel1a-header"
            >
              <TuneIcon style={{ marginRight: 10 }}></TuneIcon>
              <Typography style={{ marginTop: 1 }}>
                {Vocabulary.options}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>{renderFilterForm()}</AccordionDetails>
          </Accordion>
        ) : (
          renderFilterForm()
        )}
      </Grid>
      <Grid item lg={10} md={10} sm={12} xs={12}>
        <ThemeProvider theme={getMuiTheme()}>
          <MUIDataTable
            title=""
            data={vinRequests}
            columns={columns}
            options={options}
            components={{
              TableToolbar: (_: any) => <div />,
            }}
          />

          {renderModal()}
        </ThemeProvider>
      </Grid>
    </Grid>
  );
}
